import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { trpcClient } from "@/lib/trpc";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { Save } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

const toolingItemSchema = z.object({
  code: z.string().min(1, "Mã CCDC là bắt buộc"),
  name: z.string().min(1, "Tên CCDC là bắt buộc"),
  category: z.string().optional().nullable(),
  unit: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
});

type ToolingItemFormValues = z.infer<typeof toolingItemSchema>;

interface ToolingItemFormProps {
  initialData?: any;
  isEdit?: boolean;
}

export function ToolingItemForm({ initialData, isEdit }: ToolingItemFormProps) {
  const navigate = useNavigate();

  const form = useForm<ToolingItemFormValues>({
    resolver: zodResolver(toolingItemSchema),
    defaultValues: {
      code: initialData?.code || "",
      name: initialData?.name || "",
      category: initialData?.category || null,
      unit: initialData?.unit || "Cái",
      description: initialData?.description || "",
      status: initialData?.status || "ACTIVE",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ToolingItemFormValues) =>
      trpcClient.tooling.createItem.mutate(data),
    onSuccess: () => {
      toast.success("Tạo CCDC thành công");
      navigate({ to: "/master/tooling" });
    },
    onError: (error: any) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (variables: { id: string; data: ToolingItemFormValues }) =>
      trpcClient.tooling.updateItem.mutate(variables),
    onSuccess: () => {
      toast.success("Cập nhật CCDC thành công");
      navigate({ to: "/master/tooling" });
    },
    onError: (error: any) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });

  const onSubmit = (data: ToolingItemFormValues) => {
    if (isEdit && initialData?.id) {
      updateMutation.mutate({ id: initialData.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Công cụ / Dụng cụ", to: "/master/tooling" },
        { label: isEdit ? (initialData?.name || "Chỉnh sửa") : "Tạo mới" },
      ]}
      title={isEdit ? `Sửa ${initialData?.code || initialData?.name || ""}` : "Thêm Công Cụ"}
      onBack={() => navigate({ to: "/master/tooling" })}
      showContextPanel={false}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: "/master/tooling" })}
          >
            Hủy
          </Button>
          <Button
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save className="mr-1.5 h-4 w-4" />
            {isPending ? "Đang lưu..." : "Lưu lại"}
          </Button>
        </div>
      }
    >
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Thông tin cơ bản</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Mã CCDC <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Nhập mã CCDC..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Tên CCDC <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Nhập tên CCDC..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phân loại</FormLabel>
                    <FormControl>
                      <ComboboxConfigMaster
                        categoryCode="TOOLING_CATEGORY"
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Chọn phân loại"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Đơn vị tính</FormLabel>
                    <FormControl>
                      <ComboboxConfigMaster
                        categoryCode="UNIT_TOOLING"
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Chọn đơn vị tính"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="md:col-span-2">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mô tả</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Nhập mô tả thêm..."
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  </StandardDetailLayout>
  );
}
